import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const menu = {
  Antipasti: [
    { name: "Prosciutto di Parma DOP 18 mesi", price: "11,00" },
    { name: "Salame d'la duja", note: "salame crudo conservato sotto grasso", price: "7,00" },
    { name: "Mocetta di manzo", note: "carne di manzo stagionata", price: "10,00" },
    { name: "Lardo Biellese", price: "6,00" },
    { name: "Fiocco di maiale stagionato", note: "da produttore locale", price: "10,00" },
    { name: "Piatto di salumi misti", price: "12,00" },
    { name: "Vitello tonnato", price: "12,00" },
    { name: "Cotechino con purè di patate", price: "6,00" },
    { name: "Antipasti caldi", note: "cinque assaggi di torte salate, frittatina e cotechino", price: "7,00" },
    { name: "Antipasti freddi", price: "12,00" },
    { name: "Antipasti misti", note: "salumi, antipasti caldi e freddi", price: "25,00" },
    { name: "Acciughe con salsa verde", price: "12,00" },
  ],
  Primi: [
    { name: "Risotto in “Cagnone”", note: "riso Sant'Andrea mantecato con burro dorato e formaggi tipici locali", price: "12,00" },
    { name: "Panissa", note: "risotto al vino rosso con salame, fagioli e lardo, mantecato con burro e parmigiano", price: "12,00" },
    { name: "Agnolotti di carne*", note: "fatti da noi, al sugo d'arrosto", price: "11,00" },
    { name: "Ravioli di magro*", note: "ricotta e spinaci fatti da noi, burro e salvia", price: "11,00" },
    { name: "Gnocchi di patate*", note: "fatti da noi — burro e salvia, ragù o pomodoro", price: "11,00" },
  ],
  Secondi: [
    { name: "Bollito misto", note: "manzo, lingua, testina, coscia di pollo, cotechino con vassoio di salse", price: "20,00" },
    { name: "Paletta di Coggiola", note: "prosciutto di spalla aromatizzato con purè di patate", price: "15,00" },
    { name: "Brasato al vino con polenta", note: "manzo cotto nel vino rosso, polenta di farine locali", price: "16,00" },
    { name: "Fonduta con crostini", price: "14,00" },
    { name: "Sottofiletto ai ferri", price: "20,00" },
    { name: "Lumache* alla parigina", note: "porzione da 6 · €8,00 — porzione da 12 · €16,00", price: "8/16" },
  ],
  Contorni: [
    { name: "Verdure lesse o saltate al burro", price: "5,00" },
    { name: "Insalata mista", price: "5,00" },
    { name: "Piatto misto di formaggi locali", note: "con marmellate", price: "12,00" },
  ],
  Dolci: [
    { name: "Bonet", note: "dolce di cioccolato e amaretti", price: "6,00" },
    { name: "Crème caramel", price: "6,00" },
    { name: "Meringata con cioccolato fondente*", price: "6,00" },
    { name: "Pannacotta", price: "6,00" },
    { name: "Gelato", note: "limone o crema", price: "6,00" },
    { name: "Torcetti Biellesi con ciliegie e crema di Ratafià", price: "6,00" },
    { name: "Torta di nocciole", price: "6,00" },
    { name: "Misto di dolci", note: "torta nocciole, bonet, panna cotta, crème caramel", price: "11,00" },
  ],
  "Vini & Bevande": [
    { name: "Moscato d'Asti", note: "bottiglia cl. 37,5 · €10,00 — bottiglia cl. 75 · €18,00", price: "10/18" },
    { name: "Passito di Erbaluce di Caluso", note: "bicchiere · €5,00 — bottiglia cl. 50 · €28,00", price: "5/28" },
    { name: "Barolo Chinato", note: "al bicchiere", price: "5,00" },
    { name: "Acqua", note: "0,5 l · €2,50 — 1 l · €3,50", price: "2,50/3,50" },
    { name: "Birra Menabrea bionda 0,33 l", price: "4,00" },
    { name: "Coca-Cola / Sprite / Fanta 0,33 l", price: "3,00" },
    { name: "Caffè", price: "2,00" },
  ],
};

const categories = Object.keys(menu);

const Menu = () => {
  const [active, setActive] = useState(categories[0]);

  return (
    <section id="menu" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Il menù completo</Eyebrow>
            <h2 className="mt-6 font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="menu-title">
              La carta della <em className="text-[#7A1C28]">tradizione.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#6E6A66] font-light">
              Prezzi onesti, piatti di sempre. Tutto quello che esce dalla nostra cucina, sera dopo sera.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Categorie del menù">
            {categories.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={active === cat}
                data-testid={`menu-tab-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`rounded-full px-5 py-2.5 font-mono-accent text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  active === cat
                    ? "bg-[#1C1917] text-[#FAF8F5]"
                    : "border border-[#1C1917]/15 text-[#6E6A66] hover:border-[#1C1917] hover:text-[#1C1917]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.ul
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-2 gap-x-16"
              data-testid="menu-list"
            >
              {menu[active].map((item) => (
                <li key={item.name} className="border-b border-[#E7E4DF] py-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif-display text-lg sm:text-xl leading-snug">{item.name}</h3>
                    <span className="shrink-0 font-mono-accent text-sm text-[#B87F1B]">€ {item.price}</span>
                  </div>
                  {item.note && (
                    <p className="mt-1.5 text-sm text-[#6E6A66] font-light">{item.note}</p>
                  )}
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-2">
          <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#6E6A66]" data-testid="menu-frozen-note">
            * I prodotti possono essere congelati
          </p>
          <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#6E6A66]" data-testid="menu-cover-charge">
            Coperto € 2,50 a persona
          </p>
        </div>
      </div>
    </section>
  );
};

export default Menu;
